import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { showModalSuccess } from "@src/redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";
import { toast } from "react-toastify";

type RequestParams = Record<string, any>;
export type DecisionStatus = "APPROVED" | "REJECTED";

export interface ProjectPayload {
  titulo?: string;
  [key: string]: any;
}

export interface ProjectsState {
  active: any;
  past: any;
  requests: any;
  suggestions: any[];
  params: RequestParams;
  current?: any;
}

const defaultPageShape = () => ({
  data: [],
  meta: {
    total: 0,
    last_page: 1
  }
});

export const getActiveProjects = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("projects/getActive", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    status: 'active',
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  //TODO: get only active projects
  const response = await api.get("api/projects", { params: query });
  return {
    data: response.data,
    params: query
  };
});

export const getPastProjects = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("projects/getPast", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    status: 'inactive',
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get("api/projects", { params: query });
  return {
    data: response.data,
    params: query
  };
});

export const getContractorRequests = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("projects/getContractorRequests", async (params = {}, { getState }) => {
  const state = getState();
  const contractorId = state.auth?.userData?.role_id;

  const query = {
    ...params,
    status: 'INVITATION'
  };


  const response = await api.get(`api/contractors/${contractorId}/projects`, { params: query });
  return {
    data: response.data,
    params: query
  };
});

export const getCompanyProjectContractors = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("projects/getCompanyProjectContractors", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};
  const companyId = state.auth?.userData?.role_id;

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(`api/companies/${companyId}/projectsContractors`, {
    params: query
  });
  return {
    data: response.data,
    params: query
  };
});

export const joinProject = createAsyncThunk<
  any,
  string | number,
  { state: RootState; rejectValue: string }
>("projects/joinProject", async (projectId, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const contractorId = state.auth?.userData?.role_id;

    const response = await api.post(
      `api/contractors/${contractorId}/projects`,
      {
        project_id: projectId
      }
    );

    return {
      data: response.data
    };
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 404) return rejectWithValue("Proyecto no encontrado.");
    if (status === 400) return rejectWithValue("Solicitud inválida.");
    if (status === 409) return rejectWithValue("Ya estás unido a este proyecto.");
    return rejectWithValue(
      error?.response?.data?.message || "No fue posible unirse al proyecto."
    );
  }
});

export const updateContractorProjectStatus = createAsyncThunk<
  any,
  { projectId: number | string; status: DecisionStatus },
  { state: RootState; rejectValue: string }
>(
  "projects/updateContractorProjectStatus",
  async ({ projectId, status }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const contractorId = state.auth?.userData?.role_id;

      const response = await api.post(
        `api/projects/${projectId}/contractors/${contractorId}/status`,
        { status }
      );

      return {
        data: response.data,
        status
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "No fue posible actualizar el estatus del proyecto."
      );
    }
  }
);

export const getProjectSuggestions = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("projects/getSuggestions", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get("api/projects/suggestions", { params: query });
  return {
    data: response.data
  };
});

export const respondContractorRequest = createAsyncThunk<
  any,
  { id: number | string; decision: DecisionStatus },
  { dispatch: AppDispatch }
>("projects/respondRequest", async ({ id, decision }, { dispatch }) => {
  const response = await api.post(
    `api/projects/requests/${id}/${decision}`,
    {}
  );

  if (response.data.status === "success") {
    await dispatch(
      showModalSuccess({
        message: response.data.message
      })
    );
    dispatch(getContractorRequests());
  }

  return {
    data: response.data
  };
});

export const createProject = createAsyncThunk<
  any,
  any,
  { dispatch: AppDispatch; state: RootState }
>("projects/create", async (payload, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const contractors = (payload.contractors || payload.contratistas || [])
    .map((contractor: any) => ({
      id: contractor?.id ?? contractor?.contractor_id ?? contractor?.contractor?.value,
      contract_value:
        contractor?.contract_value ??
        contractor?.contract_values ??
        contractor?.valor_contrato,
      insured_amounts_id:
        contractor?.insured_amounts_id ??
        contractor?.suma_asegurada ??
        contractor?.insured_amount_id
    }))
    .filter((contractor: any) => contractor?.id);

  const body = {
    company_id: payload.company_id,
    company_client_id: payload.company_client_id,
    project_name: payload.project_name,
    status: true,
    start_date: payload.start_date,
    end_date: payload.end_date,
    address: payload.address,
    postal_code: payload.postal_code,
    details: payload.details,
    certificate_id: payload.certificate_id,
    title: payload.title,
    description: payload.description,
    contractors,
    product_id: productSelected?.value,
    business_line_id: businessLineSelected?.id
  };

  const response = await api.post("api/projects", body);

  if (response.data.status === "success") {
    await dispatch(
      showModalSuccess({ message: response.data.message, url: "/rc/projects" })
    );
  }

  return {
    data: response.data
  };
});

export const getProjectById = createAsyncThunk<any, number | string>(
  "projects/getById",
  async (id, { getState }) => {
    const state = getState() as RootState;
    const { productSelected } = state.products || {};
    const { businessLineSelected } = state.businessLines || {};

    const params = {
      product: productSelected?.value,
      business_line: businessLineSelected?.id
    };

    const response = await api.get(`api/projects/${id}`, { params });
    return {
      data: response.data
    };
  }
);

export const updateProject = createAsyncThunk<
  any,
  { id: number | string; data: any },
  { dispatch: AppDispatch; state: RootState }
>("projects/update", async ({ id, data }, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const contractors = (data.contractors || data.contratistas || [])
    .map((contractor: any) => ({
      id: contractor?.id ?? contractor?.contractor_id ?? contractor?.contractor?.value,
      contract_value:
        contractor?.contract_value ??
        contractor?.contract_values ??
        contractor?.valor_contrato,
      insured_amounts_id:
        contractor?.insured_amounts_id ??
        contractor?.suma_asegurada ??
        contractor?.insured_amount_id
    }))
    .filter((contractor: any) => contractor?.id);

  const body = {
    company_id: data.company_id,
    company_client_id: data.company_client_id,
    project_name: data.project_name,
    status: data.status,
    start_date: data.start_date,
    end_date: data.end_date,
    address: data.address,
    postal_code: data.postal_code,
    details: data.details,
    certificate_id: data.certificate_id,
    title: data.title,
    description: data.description,
    contractors,
    product_id: data.product_id ?? productSelected?.value,
    business_line_id: data.business_line_id ?? businessLineSelected?.id
  };

  const response = await api.put(`api/projects/${id}`, body);

  if (response.data.status === "success") {
    await dispatch(
      showModalSuccess({ message: response.data.message, url: "/rc/projects" })
    );
  }

  return {
    data: response.data
  };
});

export const deleteProject = createAsyncThunk<
  any,
  number,
  { dispatch: AppDispatch }
>("projects/delete", async (id, { dispatch }) => {
  const response = await api.delete(`api/projects/${id}`);

  if (response.data.status === "success") {
    dispatch(showModalSuccess({ message: response.data.message }));
    await dispatch(getActiveProjects());
  }

  return {
    data: response.data
  };
});

const initialState: ProjectsState = {
  active: defaultPageShape(),
  past: defaultPageShape(),
  requests: defaultPageShape(),
  suggestions: [],
  params: {},
  current: null
};

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    resetProjects: (state) => {
      state.active = defaultPageShape();
      state.past = defaultPageShape();
      state.requests = defaultPageShape();
      state.params = {};
      state.suggestions = [];
      state.current = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getActiveProjects.fulfilled, (state, action) => {
      state.active = action.payload.data;
      state.params = action.payload.params;
    });
    builder.addCase(getPastProjects.fulfilled, (state, action) => {
      state.past = action.payload.data;
      state.params = action.payload.params;
    });
    builder.addCase(getContractorRequests.fulfilled, (state, action) => {
      state.requests = action.payload.data;
      state.params = action.payload.params;
    });
    builder.addCase(getCompanyProjectContractors.fulfilled, (state, action) => {
      state.requests = action.payload.data;
      state.params = action.payload.params;
    });
    builder.addCase(getProjectSuggestions.fulfilled, (state, action) => {
      state.suggestions = action.payload.data;
    });
    builder.addCase(getProjectById.fulfilled, (state, action) => {
      state.current = action.payload.data?.data ?? null;
    });
    builder.addCase(joinProject.fulfilled, (_, action) => {
      toast.success(action.payload?.data?.message || "Solicitud enviada con éxito.", {
        hideProgressBar: true,
        className: "toast-success"
      });
    });
    builder.addCase(joinProject.rejected, (_, action) => {
      toast.error((action.payload as string) || "No fue posible unirse al proyecto.", {
        hideProgressBar: true,
        className: "toast-danger"
      });
    });
    builder.addCase(updateContractorProjectStatus.fulfilled, (_, action) => {
      toast.success(
        action.payload?.data?.message ||
          (action.payload?.status === "APPROVED"
            ? "Proyecto aceptado con éxito."
            : "Proyecto rechazado con éxito."),
        {
          hideProgressBar: true,
          className: "toast-success"
        }
      );
    });
    builder.addCase(updateContractorProjectStatus.rejected, (_, action) => {
      toast.error(
        (action.payload as string) ||
          "No fue posible actualizar el estatus del proyecto.",
        {
          hideProgressBar: true,
          className: "toast-danger"
        }
      );
    });
  }
});

export const { resetProjects } = projectsSlice.actions;

export default projectsSlice.reducer;
